import { Navigate, useLocation } from "react-router-dom";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import { getDepartmentConfig } from "../../../Departments/utils/departmentConfig";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import { useEffect, useState } from "react";
import Tabs from "../../../DesignSystem/Tabs/Tabs";
import OutlinedStatusButton from "../../../DesignSystem/Buttons/OutlinedStatusButtons";
import { LICENCE_TYPES } from "../../../Marketing/MarketingDashabord/constants/emailLicenceTypes";
import "../../../../css/Marketing/WebsiteCharges.css";
import { getCheckPrice } from "../../AMLStatements.js/Utils/checkPricing";
import { getParaplanningCatalogs } from "../../../Paraplanning/services/paraplanningChargesService";
import {
  BILLING_TYPE_LABELS,
  BILLING_FREQUENCY_LABELS,
  GUIDELINE_TABS,
  MONTHLY_FEES,
  WEBSITE_CHARGES,
  TECHNOLOGY_CHARGES,
  AML_CHECK_TYPES,
  OTHER_AML_TOOLS,
  ACTION_STATUSES,
} from "../constants/guidelineCharges";

const money = (amount, currency = "GBP") =>
  amount == null
    ? "Not configured"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(
        amount,
      );

const chargeAmount = (item) =>
  `${item.amountPrefix || ""}${item.amountType === "percentage" ? `${item.amount}%` : money(item.amount, item.currency || "GBP")}`;

const GuidelineTable = ({ headers, rows, label }) => (
  <section className="website-dashboard-card table-scroll-card">
  <div className="website-table-wrap table-rows-scroll-container table-scroll-container">
    <table className="website-table" aria-label={label}>
      <thead>
        <tr>
          {headers.map((header) => (
            <th
              key={header}
              className="FP_Help_Text_Semibold"
              style={
                header === "Amount" || header === "Base Cost"
                  ? { textAlign: "right" }
                  : undefined
              }
            >
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length ? (
          rows.map((row) => (
            <tr key={row.id}>
              {row.cells.map((cell, index) => (
                <td
                  key={index}
                  className="FP_Help_Text black"
                  style={
                    headers[index] === "Amount" ||
                    headers[index] === "Base Cost"
                      ? { textAlign: "right", whiteSpace: "nowrap" }
                      : undefined
                  }
                >
                  {headers[0] === "Status" && index === 0 ? (
                    <OutlinedStatusButton
                      label={cell}
                      color={row.color}
                    />
                  ) : cell || "-"}
                </td>
              ))}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={headers.length}>No charges available.</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
  </section>
);

const GuidelineContent = () => {
  const [activeTab, setActiveTab] = useState("action-statuses");
  const [catalogs, setCatalogs] = useState(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (activeTab !== "paraplanning" || catalogs !== null) return;
    let cancelled = false;
    setError(false);
    getParaplanningCatalogs()
      .then((data) => {
        if (!cancelled) setCatalogs(data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, catalogs, attempt]);

  let headers;
  let rows;
  if (activeTab === "action-statuses") {
    headers = ["Status", "Meaning"];
    rows = ACTION_STATUSES.map((row) => ({ id: row.id, color: row.color, cells: [row.label, row.meaning] }));
  } else if (activeTab === "monthly") {
    headers = ["Adviser Permission Criteria", "Billing Type", "Billing Frequency", "Amount"];
    rows = MONTHLY_FEES.map((row) => ({
      id: row.id,
      cells: [row.permission, BILLING_TYPE_LABELS[row.billingType], BILLING_FREQUENCY_LABELS[row.billingFrequency], money(row.amount)],
    }));
  } else if (activeTab === "aml") {
    headers = ["Tool", "Check Type", "Amount"];
    rows = [
      ...AML_CHECK_TYPES.map((checkType) => ({
        id: checkType,
        tool: "Check²",
        checkType,
        amount: getCheckPrice(checkType),
      })),
      ...OTHER_AML_TOOLS,
    ].map((row) => ({
      id: row.id,
      cells: [row.tool, row.checkType, money(row.amount)],
    }));
  } else if (activeTab !== "paraplanning") {
    headers = [
      "Category",
      "Description / Type",
      "Billing Type", "Billing Frequency",
      "From Date",
      "Amount",
    ];
    const technologyCharges = [
      ...TECHNOLOGY_CHARGES,
      ...LICENCE_TYPES.map((licence) => ({
        id: `email-${licence.id}`,
        category: "Email Licences",
        description: `${licence.id} (per licence)`,
        billingType: licence.billingType,
        billingFrequency: licence.billingFrequency,
        fromDate: null,
        amount: licence.monthlyRate,
      })),
    ];
    rows = (activeTab === "website" ? WEBSITE_CHARGES : technologyCharges).map(
      (row) => ({
        id: row.id,
        cells: [
          row.category,
          row.description,
          BILLING_TYPE_LABELS[row.billingType], BILLING_FREQUENCY_LABELS[row.billingFrequency],
          row.fromDate
            ? new Date(`${row.fromDate}T00:00:00`).toLocaleDateString("en-GB", {
                month: "short",
                year: "numeric",
              })
            : "",
          money(row.amount),
        ],
      }),
    );
  }

  return (
    <>
      <Tabs
        tabs={GUIDELINE_TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      {activeTab !== "paraplanning" ? (
        <GuidelineTable
          headers={headers}
          rows={rows}
          label={GUIDELINE_TABS.find((tab) => tab.id === activeTab).label}
        />
      ) : error ? (
        <div role="alert">
          Unable to load paraplanning charges.{" "}
          <button
            type="button"
            onClick={() => setAttempt((value) => value + 1)}
          >
            Retry
          </button>
        </div>
      ) : catalogs === null ? (
        <p role="status">Loading paraplanning charges…</p>
      ) : !catalogs.length ? (
        <p>No published paraplanning charges available.</p>
      ) : (
            <GuidelineTable
              label="Paraplanning charges"
              headers={[
                "Case Type",
                "Description",
                "Base Cost",
                "Additional Charges",
              ]}
              rows={catalogs.flatMap((catalog) => catalog.categories
                .filter((category) => category.isActive !== false)
                .flatMap((category) =>
                  category.items
                    .filter(
                      (item) =>
                        item.isActive !== false &&
                        item.visibleOnFrontend !== false,
                    )
                    .map((item) => ({
                      id: `${catalog.id}-${category.id}-${item.id}`,
                      cells: [
                        item.name,
                        item.description,
                        chargeAmount(item),
                        (item.modifiers || [])
                          .map(
                            (modifier) =>
                              `${modifier.label}: ${chargeAmount(modifier)}`,
                          )
                          .join("; "),
                      ],
                    })),
                ))}
            />
      )}
    </>
  );
};

const Guidelines = () => {
  const location = useLocation();
  const returnPath = location.state?.returnPath || APP_ROUTES.MONTHLY_PAYMENTS;
  const { userPermissions } = useUserPermissions();
  const { Finance } = getDepartmentConfig(
    Array.isArray(userPermissions) ? userPermissions : [],
  );
  const canAccess =
    Finance.show &&
    Finance.cards.some(
      (card) => card.route === APP_ROUTES.MONTHLY_PAYMENTS,
    );
  
  // console.log(canAccess);

  if (!canAccess) {
    return <Navigate to={APP_ROUTES.DEPARTMENT_ALL_STAFF} replace />;
  }

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Guidelines</title>
      </Helmet>
      <Sidebar isSetExpanded={true} />

      <main className="Main-Container table-contained-page table-scroll-page column gap-m ">
        <Breadcrumb
          items={[
            { label: "Finance", to: "/Department/Finance" },
            {
              label: "Monthly Payments",
              to: returnPath,
            },
            { label: "Guidelines" },
          ]}
        />

        <header className="row space-between gap-l">
          <h3 className="FP_H3">Guidelines</h3>
        </header>
        <GuidelineContent />
      </main>
    </div>
  );
};

export default Guidelines;
